// src/app/components/loan/upcoming-payments/upcoming-payments.component.ts

import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { UpcomingPaymentsService, PendingPayment } from '../../../services/upcoming-payments.service';
import { PaymentService } from '../../../services/payment.service';
import { Router } from '@angular/router';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../../../dialogs/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-upcoming-payments',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './upcoming-payments.component.html',
  styleUrls: ['./upcoming-payments.component.scss']
})
export class UpcomingPaymentsComponent implements OnChanges, OnDestroy {
  /** Loan number to fetch payments for */
  @Input() loanId!: string;
  @Output() paymentsChanged = new EventEmitter<void>();  // <-- add this

  /** Signal holding fetched pending payments */
  pending = signal<PendingPayment[] | null>(null);

  /** Set to track payments being canceled */
  private cancelingSet = new Set<number>();

  /** Column order for the table display */
  readonly displayedColumns = [
    'rowId',
    'type',
    'inputDate',
    'nextBillingDate',
    'amount',
    'actions'
  ];

  private sub?: Subscription;

  constructor(
    private svc: UpcomingPaymentsService,
    private svctwo: PaymentService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnChanges(): void {
    this.sub?.unsubscribe();
    if (this.loanId) {
      this.sub = this.svc.getPendingForLoan(this.loanId).subscribe(
        items => this.pending.set(items),
        err   => {
          console.error('Error loading pending payments', err);
          this.pending.set([]);
        }
      );
    } else {
      this.pending.set(null);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Check if a specific payment is in canceling state */
  isCanceling(item: PendingPayment): boolean {
    return this.cancelingSet.has(item.rowId);
  }

  /** Cancel a pending payment and track its state */
  onCancel(item: PendingPayment): void {
    if (this.isCanceling(item)) {
      return;
    }

    // format the date instead of using a pipe in TS
    const when = item.nextDate
      ? formatDate(item.nextDate, 'shortDate', 'en-US')
      : 'N/A';

const data: ConfirmationDialogData = {
  title: 'Cancel Scheduled Payment?',
  message: `Are you sure you want to cancel the payment of $${item.amountFixed.toFixed(2)} on ${when}?`,
  typeLabel: item.billingType === 1 ? 'One-time'   : 'Recurring',
  idLabel:   item.rowId.toString(),
  confirmText: 'Yes, Cancel',
  cancelText:  'No, Keep'
};


    this.dialog
      .open(ConfirmationDialogComponent, { width: '380px', data })
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.cancelingSet.add(item.rowId);
        this.svctwo.cancelRecurringPayment(item.rowId).subscribe(
          () => {
            this.cancelingSet.delete(item.rowId);
            this.refreshPending();
            this.paymentsChanged.emit();                      // <-- tell parent something changed
          },
          err => {
            console.error('Error canceling payment', err);
            this.cancelingSet.delete(item.rowId);
          }
        );
      });
  }

  private extractWalletIdFromNotes(notes?: string | null): string | undefined {
  if (!notes) return;
  const trimmed = notes.trim();
  // if notes is just the id, use it; otherwise pick the first 4+ digit number
  if (/^\d+$/.test(trimmed)) return trimmed;
  const m = trimmed.match(/(\d{4,})/);
  return m?.[1];
}

  /** Navigate to the same payment page, in “edit” mode */
// upcoming-payments.component.ts
onEdit(item: PendingPayment): void {
  const walletId = this.extractWalletIdFromNotes(item.notes);

  const queryParams: any = {
    paymentId: item.rowId,
    billingType: item.billingType,
    ...(walletId ? { walletId } : {})
  };

  // Only force autoPay when recurring
  if (item.billingType !== 1) {
    queryParams.autoPay = 'true';
  }

  this.router.navigate(
    ['/loan', this.loanId, 'payment'],
    { queryParams }
  );
}


  /** Refresh pending list */
  private refreshPending(): void {
    if (!this.loanId) {
      return;
    }
    this.svc.getPendingForLoan(this.loanId).subscribe(
      items => this.pending.set(items),
      err   => {
        console.error('Error reloading pending payments', err);
        this.pending.set([]);
      }
    );
  }
}
